package com.ashes.dev.ai.weather.weathershow

import androidx.compose.ui.graphics.Color
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import io.ktor.client.*
import io.ktor.client.call.*
import io.ktor.client.plugins.contentnegotiation.*
import io.ktor.client.request.*
import io.ktor.serialization.kotlinx.json.*
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

class WeatherViewModel : ViewModel() {
    private val _uiState = MutableStateFlow(WeatherUIState())
    val uiState = _uiState.asStateFlow()

    private val client = HttpClient {
        install(ContentNegotiation) {
            json(Json {
                ignoreUnknownKeys = true
                prettyPrint = true
                isLenient = true
            })
        }
    }

    private val locationProvider by lazy { getLocationProvider() }
    private var updateJob: Job? = null
    private var timeJob: Job? = null

    init {
        startTimeUpdates()
        refreshWeather()
        toggleAutoUpdate(false)
    }

    private fun startTimeUpdates() {
        timeJob?.cancel()
        timeJob = viewModelScope.launch {
            while (true) {
                try {
                    _uiState.update { it.copy(currentTime = getCurrentTime()) }
                } catch (t: Throwable) {
                    _uiState.update { it.copy(currentTime = "--:--") }
                }
                delay(1000)
            }
        }
    }

    fun updateThemeColor(color: Color) {
        _uiState.update { it.copy(themeColor = color) }
    }

    fun toggleAutoUpdate(enabled: Boolean) {
        _uiState.update { it.copy(isAutoUpdateEnabled = enabled) }
        updateJob?.cancel()
        if (enabled) {
            updateJob = viewModelScope.launch {
                while (true) {
                    refreshWeather()
                    delay(60_000)
                }
            }
        }
    }

    fun refreshWeather() {
        viewModelScope.launch {
            fetchWeatherForCurrentLocation()
        }
    }

    fun showStatus(message: String) {
        viewModelScope.launch {
            _uiState.update { it.copy(statusMessage = message) }
            delay(3000)
            _uiState.update { it.copy(statusMessage = null) }
        }
    }

    fun searchLocation(address: String) {
        if (address.isBlank()) return
        println("WeatherViewModel: Searching for address: $address")
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null) }
            try {
                val location = locationProvider.getCoordinatesFromAddress(address)
                if (location != null) {
                    println("WeatherViewModel: Found coordinates: ${location.latitude}, ${location.longitude}")
                    fetchWeather(location.latitude, location.longitude)
                    _uiState.update { it.copy(address = address) }
                } else {
                    println("WeatherViewModel: Location returned null for address: $address")
                    _uiState.update { it.copy(isLoading = false) }
                    showStatus("Address not found!")
                }
            } catch (t: Throwable) {
                println("WeatherViewModel: Search error: ${t.message}")
                t.printStackTrace()
                _uiState.update { it.copy(isLoading = false, error = "Search Error: ${t.message}") }
                showStatus("Error searching location")
            }
        }
    }

    fun fetchWeatherForManualLocation(latitude: Double, longitude: Double) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true, error = null, address = "Searching...") }
            fetchWeather(latitude, longitude)
            try {
                val address = locationProvider.getAddressFromLocation(latitude, longitude)
                if (address != null) {
                    _uiState.update { it.copy(address = address) }
                }
            } catch (t: Throwable) {
                _uiState.update { it.copy(address = "Custom Location") }
            }
        }
    }

    private suspend fun fetchWeatherForCurrentLocation() {
        _uiState.update { it.copy(isLoading = true, error = null) }
        try {
            val location = locationProvider.getCurrentLocation()
            if (location != null) {
                fetchWeather(location.latitude, location.longitude)
                viewModelScope.launch {
                    try {
                        val address = locationProvider.getAddressFromLocation(location.latitude, location.longitude)
                        if (address != null) {
                            _uiState.update { it.copy(address = address) }
                        }
                    } catch (t: Throwable) {
                        _uiState.update { it.copy(address = "My Location") }
                    }
                }
            } else {
                _uiState.update { it.copy(address = "London, UK") }
                fetchWeather(51.5074, -0.1278)
            }
        } catch (e: Exception) {
            if (e.message == "GPS_DISABLED") {
                _uiState.update { it.copy(isLoading = false, error = "GPS is disabled. Please enable it to get local weather.") }
            } else {
                _uiState.update { it.copy(isLoading = false, error = "Location Error: ${e.message}") }
            }
        }
    }

    private suspend fun fetchWeather(latitude: Double, longitude: Double) {
        println("WeatherViewModel: Fetching weather for $latitude, $longitude")
        try {
            val response: WeatherResponse = client.get("https://api.open-meteo.com/v1/forecast") {
                parameter("latitude", latitude)
                parameter("longitude", longitude)
                parameter("current_weather", true)
                parameter("daily", "temperature_2m_max,temperature_2m_min,weathercode,sunrise,sunset")
                parameter("timezone", "auto")
            }.body()

            val lastUpdatedTime = getCurrentTime().split(":").take(2).joinToString(":")
            val lastUpdated = "Last updated: $lastUpdatedTime"

            val forecastDays = response.daily?.let { daily ->
                daily.time.indices.map { i ->
                    ForecastDay(
                        date = getFormattedDate(daily.time[i]),
                        maxTemp = daily.maxTemp[i],
                        minTemp = daily.minTemp[i],
                        weatherCode = daily.weatherCode[i]
                    )
                }
            } ?: emptyList()

            _uiState.update {
                it.copy(
                    temperature = response.currentWeather?.temperature ?: 0.0,
                    weatherCode = response.currentWeather?.weatherCode ?: 0,
                    sunrise = response.daily?.sunrise?.firstOrNull()?.split("T")?.lastOrNull() ?: "--:--",
                    sunset = response.daily?.sunset?.firstOrNull()?.split("T")?.lastOrNull() ?: "--:--",
                    forecast = forecastDays,
                    lastUpdated = lastUpdated,
                    isLoading = false,
                    lastLat = latitude,
                    lastLon = longitude
                )
            }
        } catch (t: Throwable) {
            println("WeatherViewModel: Weather error: ${t.message}")
            t.printStackTrace()
            _uiState.update { it.copy(isLoading = false, error = "Weather Error: ${t.message ?: "Check connection"}") }
        }
    }

    override fun onCleared() {
        client.close()
        updateJob?.cancel()
        timeJob?.cancel()
        super.onCleared()
    }
}
